import { supabase } from '../lib/supabase';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuditLog } from './useAuditLog';

export function useRoleManagement() {
  const { user } = useAuth0();
  const { logAction } = useAuditLog();

  const changeUserRole = async (
    userId: number,
    currentRole: 'ADMIN' | 'FINANCE',
    newRole: 'ADMIN' | 'FINANCE',
    userData: {
      auth0ID: string;
      email: string;
      firstname: string;
      lastname: string;
      mobilenumber: string;
      status: 'PENDING' | 'APPROVED' | 'DENIED';
    }
  ) => {
    if (currentRole === newRole) {
      throw new Error('User already has this role');
    }

    try {
      // Start a transaction-like operation
      // First, insert into the new role table
      if (newRole === 'ADMIN') {
        const { error: insertError } = await supabase
          .from('admins')
          .insert({
            auth0ID: userData.auth0ID,
            email: userData.email,
            firstname: userData.firstname,
            lastname: userData.lastname,
            mobilenumber: userData.mobilenumber,
            status: userData.status
          });

        if (insertError) throw insertError;
      } else {
        const { error: insertError } = await supabase
          .from('finance_users')
          .insert({
            auth0id: userData.auth0ID,
            email: userData.email,
            firstname: userData.firstname,
            lastname: userData.lastname,
            mobilenumber: userData.mobilenumber,
            status: userData.status
          });

        if (insertError) throw insertError;
      }

      // Then delete from the old role table
      if (currentRole === 'ADMIN') {
        const { error: deleteError } = await supabase
          .from('admins')
          .delete()
          .eq('id', userId);

        if (deleteError) {
          // Rollback - delete the newly created record
          if (newRole === 'FINANCE') {
            await supabase
              .from('finance_users')
              .delete()
              .eq('auth0ID', userData.auth0ID);
          }
          throw deleteError;
        }
      } else {
        const { error: deleteError } = await supabase
          .from('finance_users')
          .delete()
          .eq('id', userId);

        if (deleteError) {
          // Rollback - delete the newly created record
          if (newRole === 'ADMIN') {
            await supabase
              .from('admins')
              .delete()
              .eq('auth0ID', userData.auth0ID);
          }
          throw deleteError;
        }
      }

      // Log the role change action
      await logAction(
        'role_change',
        'admin_management',
        {
          resourceType: 'user',
          resourceId: userId.toString(),
          details: {
            user_email: userData.email,
            user_name: `${userData.firstname} ${userData.lastname}`,
            previous_role: currentRole,
            new_role: newRole,
            changed_by: user?.email
          },
          success: true
        }
      );

      return { success: true };
    } catch (error) {
      console.error('Error changing user role:', error);
      
      // Log the failed attempt
      await logAction(
        'role_change',
        'admin_management',
        {
          resourceType: 'user',
          resourceId: userId.toString(),
          details: {
            user_email: userData.email,
            user_name: `${userData.firstname} ${userData.lastname}`,
            previous_role: currentRole,
            new_role: newRole,
            changed_by: user?.email,
            error: error instanceof Error ? error.message : 'Unknown error'
          },
          success: false
        }
      );

      throw error;
    }
  };

  return {
    changeUserRole
  };
}
